import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Printer, ArrowLeft, ShieldCheck, Loader2, AlertTriangle, Download, RotateCcw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';

const GST_STATE_CODES = {
  "01": "Jammu and Kashmir", "02": "Himachal Pradesh", "03": "Punjab", "04": "Chandigarh",
  "05": "Uttarakhand", "06": "Haryana", "07": "Delhi", "08": "Rajasthan", "09": "Uttar Pradesh",
  "10": "Bihar", "11": "Sikkim", "12": "Arunachal Pradesh", "13": "Nagaland", "14": "Manipur",
  "15": "Mizoram", "16": "Tripura", "17": "Meghalaya", "18": "Assam", "19": "West Bengal",
  "20": "Jharkhand", "21": "Odisha", "22": "Chhattisgarh", "23": "Madhya Pradesh", "24": "Gujarat",
  "26": "Dadra & Nagar Haveli and Daman & Diu", "27": "Maharashtra", "29": "Karnataka",
  "30": "Goa", "31": "Lakshadweep", "32": "Kerala", "33": "Tamil Nadu", "34": "Puducherry",
  "35": "Andaman and Nicobar Islands", "36": "Telangana", "37": "Andhra Pradesh", "38": "Ladakh",
  "97": "Other Territory", "99": "Center Jurisdiction"
};

const numberToWords = (num) => {
  const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const makeWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + a[n % 10] : '');
    if (n < 1000) return a[Math.floor(n / 100)] + 'Hundred ' + (n % 100 !== 0 ? 'and ' + makeWords(n % 100) : '');
    if (n < 100000) return makeWords(Math.floor(n / 1000)) + 'Thousand ' + (n % 1000 !== 0 ? makeWords(n % 1000) : '');
    if (n < 10000000) return makeWords(Math.floor(n / 100000)) + 'Lakh ' + (n % 100000 !== 0 ? makeWords(n % 100000) : '');
    return makeWords(Math.floor(n / 10000000)) + 'Crore ' + (n % 10000000 !== 0 ? makeWords(n % 10000000) : '');
  };
  return makeWords(Math.round(num)) + 'Rupees Only';
};

const InvoiceViewer = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { apiCall } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [creditNote, setCreditNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [issuingCN, setIssuingCN] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const [invoiceData, cnData] = await Promise.all([
          apiCall(`/api/orders/${orderId}/invoice`),
          apiCall(`/api/invoices/${orderId}/credit-note`).catch(() => null)
        ]);
        setInvoice(invoiceData);
        setCreditNote(cnData);
      } catch (err) {
        setError(err.message || 'Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };
    if (orderId) fetchInvoice();
  }, [orderId, apiCall]);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${orderId}/pdf`, {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Failed to generate PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice?.invoice_number || 'invoice'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download PDF: ' + err.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleIssueCreditNote = async () => {
    if (!window.confirm('Issue a full Credit Note for this order? This cannot be undone.')) return;
    setIssuingCN(true);
    try {
      const cn = await apiCall(`/api/invoices/${orderId}/credit-note`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'Return/Refund' })
      });
      setCreditNote(cn);
    } catch (err) {
      alert('Failed to issue credit note: ' + err.message);
    } finally {
      setIssuingCN(false);
    }
  };

  const handleDownloadCreditNotePDF = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${orderId}/credit-note/pdf`, {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${creditNote?.credit_note_number || 'credit-note'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download: ' + err.message);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !invoice) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
          <p className="text-gray-600">{error || 'Invoice data not available'}</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </DashboardLayout>
    );
  }

  const { vendor, customer, items, amounts } = invoice;

  // Group taxes by rate for breakdown display
  const groupedTaxes = {};
  if (invoice.tax_type !== 'NONE') {
    items.forEach(item => {
      const rate = item.tax_rate;
      if (rate > 0) {
        if (!groupedTaxes[rate]) groupedTaxes[rate] = { taxableAmount: 0, taxAmount: 0 };
        groupedTaxes[rate].taxableAmount += item.taxable_amount;
        groupedTaxes[rate].taxAmount += item.tax_amount;
      }
    });
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return dateStr; }
  };

  const handlePrint = () => window.print();

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-slate-100 p-2 sm:p-4 md:p-8 font-sans text-slate-900">
        {/* Action Bar */}
        <div className="max-w-4xl mx-auto mb-4 md:mb-6 flex items-center justify-between print:hidden" data-testid="invoice-actions">
          <button onClick={() => navigate(-1)} className="flex items-center text-slate-500 hover:text-indigo-600 transition font-medium text-sm">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </button>
          <div className="flex gap-2 flex-wrap justify-end">
            {!creditNote && (
              <Button variant="outline" size="sm" onClick={handleIssueCreditNote} disabled={issuingCN} className="gap-1 text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="issue-credit-note-btn">
                {issuingCN ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                Credit Note
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleDownloadPDF} disabled={downloading} className="gap-1" data-testid="download-pdf-btn">
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Download PDF
            </Button>
            <Button size="sm" onClick={handlePrint} className="gap-1" data-testid="print-invoice-btn">
              <Printer className="w-3.5 h-3.5" /> Print
            </Button>
          </div>
        </div>

        {/* Credit Note Banner */}
        {creditNote && (
          <div className="max-w-4xl mx-auto mb-4 print:hidden">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-amber-800">Credit Note Issued: {creditNote.credit_note_number}</p>
                <p className="text-xs text-amber-600">{creditNote.reason} &bull; {creditNote.amounts?.grand_total ? `\u20B9${creditNote.amounts.grand_total.toFixed(2)}` : ''}</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleDownloadCreditNotePDF} className="gap-1 text-amber-700 border-amber-300" data-testid="download-cn-pdf-btn">
                <Download className="w-3.5 h-3.5" /> CN PDF
              </Button>
            </div>
          </div>
        )}

        {/* Invoice Document */}
        <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-2xl overflow-hidden border border-slate-200 print:shadow-none print:border-none print:rounded-none" data-testid="invoice-document">

          {/* Header */}
          <div className="p-5 sm:p-8 md:p-10 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-50/50">
            <div>
              <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-indigo-600">ANIYOR</h1>
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-bold mt-1">Marketplace Platform</p>
            </div>
            <div className="sm:text-right">
              <h2 className="text-xl md:text-2xl font-black text-slate-800 uppercase">{invoice.invoice_title}</h2>
              <div className="mt-2 space-y-1">
                <p className="text-sm text-slate-500 font-bold">INV: <span className="text-slate-800">{invoice.invoice_number}</span></p>
                <p className="text-sm text-slate-500 font-bold">Order: <span className="text-slate-800">{invoice.order_number}</span></p>
                <p className="text-sm text-slate-500 font-bold">Date: <span className="text-slate-800">{formatDate(invoice.order_date)}</span></p>
              </div>
            </div>
          </div>

          <div className="p-5 sm:p-8 md:p-10">
            {/* Party Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 mb-8 md:mb-12">
              <div>
                <span className="text-[10px] uppercase font-black text-indigo-500 mb-3 block tracking-widest">Seller Details</span>
                <p className="font-bold text-lg md:text-xl text-slate-800 mb-1">{vendor.store_name}</p>
                {(vendor.address || vendor.city || vendor.state) && (
                  <p className="text-slate-500 text-sm leading-relaxed mb-3">
                    {[vendor.address, vendor.city, vendor.state, vendor.pincode].filter(Boolean).join(', ')}
                  </p>
                )}
                <div className="space-y-1">
                  <p className="text-xs font-bold text-slate-700">{vendor.tax_id_label}: <span className="text-indigo-600">{vendor.tax_id_value || 'N/A'}</span></p>
                  {vendor.gst_state_code && (
                    <p className="text-xs font-bold text-slate-700">State Code: <span className="bg-slate-100 px-2 py-0.5 rounded ml-1">{vendor.gst_state_code} - {GST_STATE_CODES[vendor.gst_state_code] || ''}</span></p>
                  )}
                </div>
              </div>
              <div>
                <span className="text-[10px] uppercase font-black text-indigo-500 mb-3 block tracking-widest">Billing & Shipping To</span>
                <p className="font-bold text-lg md:text-xl text-slate-800 mb-1">{customer.name || 'Customer'}</p>
                {(customer.address || customer.city || customer.state) && (
                  <p className="text-slate-500 text-sm leading-relaxed mb-3">
                    {[customer.address, customer.city, customer.state, customer.pincode].filter(Boolean).join(', ')}
                  </p>
                )}
                <div className="space-y-1">
                  {customer.gst_state_code && (
                    <p className="text-xs font-bold text-slate-700">Place of Supply: <span className="bg-slate-100 px-2 py-0.5 rounded ml-1">{customer.state} ({customer.gst_state_code})</span></p>
                  )}
                  {customer.email && <p className="text-xs text-slate-500">{customer.email}</p>}
                  {customer.phone && <p className="text-xs text-slate-500">{customer.phone}</p>}
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-6 md:mb-10 rounded-xl overflow-hidden border border-slate-100">
              <div className="overflow-x-auto">
                <table className="w-full text-left min-w-[520px]">
                  <thead>
                    <tr className="bg-slate-800 text-white">
                      <th className="py-3 md:py-4 px-3 md:px-5 text-xs font-bold uppercase tracking-wider">Item Description</th>
                      <th className="py-3 md:py-4 px-2 text-xs font-bold uppercase tracking-wider text-center">HSN/SAC</th>
                      <th className="py-3 md:py-4 px-2 text-xs font-bold uppercase tracking-wider text-center">Qty</th>
                      <th className="py-3 md:py-4 px-3 text-xs font-bold uppercase tracking-wider text-right">Unit Rate</th>
                      {invoice.tax_type !== 'NONE' && (
                        <th className="py-3 md:py-4 px-3 text-xs font-bold uppercase tracking-wider text-right">Tax (%)</th>
                      )}
                      <th className="py-3 md:py-4 px-3 md:px-5 text-xs font-bold uppercase tracking-wider text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((item, idx) => (
                      <tr key={idx}>
                        <td className="py-4 px-3 md:px-5">
                          <p className="font-bold text-slate-800 text-sm">{item.name}</p>
                          {item.sku && <p className="text-[10px] text-slate-400 font-bold mt-0.5">SKU: {item.sku}</p>}
                        </td>
                        <td className="py-4 px-2 text-sm text-slate-600 text-center font-medium">{item.hsn || '-'}</td>
                        <td className="py-4 px-2 text-sm text-slate-600 text-center font-medium">{item.qty}</td>
                        <td className="py-4 px-3 text-sm text-slate-600 text-right font-medium">{'\u20B9'}{item.unit_price.toFixed(2)}</td>
                        {invoice.tax_type !== 'NONE' && (
                          <td className="py-4 px-3 text-sm text-slate-600 text-right font-medium">{item.tax_rate}%</td>
                        )}
                        <td className="py-4 px-3 md:px-5 text-sm font-bold text-right text-slate-800">{'\u20B9'}{item.total.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals Section */}
            <div className="flex flex-col md:flex-row justify-between items-end gap-8 md:gap-12">
              <div className="w-full md:w-3/5">
                <div className="p-4 md:p-5 bg-slate-50 rounded-2xl border border-slate-100 mb-4">
                  <p className="text-[10px] uppercase font-black text-slate-400 mb-2 tracking-widest">Total Amount in Words</p>
                  <p className="text-sm font-bold text-indigo-900 italic">{numberToWords(amounts.grand_total)}</p>
                </div>
                <div className="flex items-start gap-3 p-3 md:p-4 bg-amber-50 rounded-xl border border-amber-100">
                  <ShieldCheck className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <div className="text-[11px] text-amber-800 leading-relaxed font-medium">
                    {invoice.gst_type === 'Composition' ? (
                      <p className="font-bold">Composition taxable person, not eligible to collect tax on supplies.</p>
                    ) : invoice.gst_type === 'Unregistered' ? (
                      <p className="font-bold">Supplier is not registered under GST. No tax is charged on this supply.</p>
                    ) : invoice.gst_type === 'Exempt' ? (
                      <p className="font-bold">Supply is exempt from GST under applicable provisions.</p>
                    ) : (
                      <p>Certified that the particulars given above are true and correct. Taxes shown are calculated as per item-wise GST rates.</p>
                    )}
                    <p className="mt-1 opacity-60 italic">This is a computer-generated document and requires no physical signature.</p>
                  </div>
                </div>
              </div>

              <div className="w-full md:w-1/3 space-y-2 md:space-y-3">
                <div className="flex justify-between text-sm text-slate-500 font-medium">
                  <span>Taxable Subtotal</span>
                  <span className="text-slate-800">{'\u20B9'}{amounts.subtotal.toFixed(2)}</span>
                </div>

                {/* Dynamic Tax Breakdown by rate */}
                {Object.keys(groupedTaxes).sort((a, b) => a - b).map(rate => (
                  <div key={rate} className="space-y-1 py-1 border-y border-slate-50">
                    {invoice.tax_type === 'CGST_SGST' ? (
                      <>
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>CGST ({rate / 2}%)</span>
                          <span className="text-slate-800 font-medium">{'\u20B9'}{(groupedTaxes[rate].taxAmount / 2).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>SGST ({rate / 2}%)</span>
                          <span className="text-slate-800 font-medium">{'\u20B9'}{(groupedTaxes[rate].taxAmount / 2).toFixed(2)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>IGST ({rate}%)</span>
                        <span className="text-slate-800 font-medium">{'\u20B9'}{groupedTaxes[rate].taxAmount.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                ))}

                {amounts.shipping > 0 && (
                  <div className="flex justify-between text-sm text-slate-500 font-medium">
                    <span>Shipping & Handling</span>
                    <span className="text-slate-800">{'\u20B9'}{amounts.shipping.toFixed(2)}</span>
                  </div>
                )}

                {amounts.discount > 0 && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm text-green-600 font-medium">
                      <span>Discount</span>
                      <span>-{'\u20B9'}{amounts.discount.toFixed(2)}</span>
                    </div>
                    {amounts.discount_codes?.length > 0 && (
                      <p className="text-[10px] text-green-500 text-right">
                        Code: {amounts.discount_codes.map(dc => dc.code || dc).join(', ')}
                      </p>
                    )}
                  </div>
                )}

                <div className="h-px bg-slate-200 my-3"></div>

                <div className="flex justify-between text-xl md:text-2xl font-black text-slate-900 items-baseline">
                  <span>Grand Total</span>
                  <div className="text-right">
                    <p className="text-indigo-600">{'\u20B9'}{Math.round(amounts.grand_total).toFixed(2)}</p>
                    <p className="text-[9px] text-slate-400 font-bold tracking-tighter uppercase mt-1">Rounded Off</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-slate-900 p-6 md:p-10 text-center">
            <p className="text-xs font-black text-slate-400 uppercase tracking-[0.4em] mb-3">ANIYOR MARKETPLACE</p>
            <div className="flex justify-center gap-3 md:gap-6 text-[10px] text-slate-500 font-bold uppercase tracking-widest flex-wrap">
              <span>Verified Seller</span>
              <span className="w-1 h-1 bg-slate-700 rounded-full my-auto"></span>
              <span>Secure Transaction</span>
              <span className="w-1 h-1 bg-slate-700 rounded-full my-auto"></span>
              <span>GST Compliant</span>
            </div>
          </div>
        </div>

        <style dangerouslySetInnerHTML={{ __html: `
          @media print {
            /* Reset everything */
            html, body { background: white !important; margin: 0 !important; padding: 0 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            
            /* Hide ALL navigation, sidebar, topbar, mobile nav, action bars */
            nav, aside, header,
            [data-testid="invoice-actions"],
            [class*="sidebar"], [class*="Sidebar"],
            [class*="mobile-nav"], [class*="MobileNav"],
            [class*="hidden lg:block"],
            .lg\\:block,
            .fixed.top-0,
            .fixed.bottom-0,
            .print\\:hidden { display: none !important; visibility: hidden !important; height: 0 !important; overflow: hidden !important; }
            
            /* Remove sidebar margin and topbar offset from main content */
            .lg\\:ml-64 { margin-left: 0 !important; }
            .lg\\:mt-16, .mt-14 { margin-top: 0 !important; }
            .mb-20, .lg\\:mb-0 { margin-bottom: 0 !important; }
            .flex.min-h-screen { display: block !important; }
            
            /* Invoice container — full width, no shadow, no border radius */
            .min-h-screen { padding: 0 !important; background: white !important; min-height: auto !important; }
            .max-w-4xl { max-width: 100% !important; border: none !important; margin: 0 !important; box-shadow: none !important; border-radius: 0 !important; }
            .shadow-xl, .shadow-lg { box-shadow: none !important; }
            .rounded-2xl, .rounded-xl, .rounded-lg { border-radius: 0 !important; }
            .bg-slate-100 { background: white !important; padding: 0 !important; }
            
            /* Compact spacing for single page */
            .p-5, .sm\\:p-8, .md\\:p-10 { padding: 20px !important; }
            .p-6, .md\\:p-10 { padding: 20px !important; }
            .mb-8, .md\\:mb-12 { margin-bottom: 12px !important; }
            .gap-8, .md\\:gap-16 { gap: 20px !important; }
            .gap-8, .md\\:gap-12 { gap: 16px !important; }
            .mb-6, .md\\:mb-10 { margin-bottom: 8px !important; }
            .space-y-2 > * + *, .md\\:space-y-3 > * + * { margin-top: 4px !important; }
            
            /* Footer compact */
            .bg-slate-900 { padding: 12px !important; }
            
            /* Page settings — A4 single page */
            @page { margin: 0.8cm; size: A4; }
          }
        `}} />
      </div>
    </DashboardLayout>
  );
};

export default InvoiceViewer;
